import React, { useState } from 'react'
import { ActivityIndicator, Dimensions, ScrollView, View } from 'react-native'

import { useNavigation, useRoute } from '@react-navigation/native'
import { B4, BottomSheet, H6, SH3, Stack, XStack } from '@zet/design-system'
import { privacyPolicyForExperianLink } from '@zet/feature-brands/utils/constants'
import { i18n } from '@zet/localization'
import { DEVICE_HEIGHT } from '@zet/utils/Constants'

import { useApiAction } from '../../api/useApiAction'
import { LoanApplicationDetailsScreenProps } from '../../types'
import StepsIndicator from '../common/StepsIndicator'
import ActionButton from '../common/actionButtons/actionButton'
import LoanHeaders from '../common/loanHeader.tsx'
import PDFViewer from '../common/pdfViewer/index.web'
import { LoadingScreen } from '../loadingScreen'

interface ScreenProps {
  props: LoanApplicationDetailsScreenProps
}

const LoanApplicationDetails = (_props: ScreenProps) => {
  const navigation = useNavigation()
  const { params } = useRoute()
  const { hash } = params ?? {}
  const {
    props: { steps, privacyNote, title, guideLines },
  } = _props

  const [showTnCBottomSheet, setShowTnCBottomSheet] = useState({
    show: false,
    uri: '',
  })
  const [showSpinner, setShowSpinner] = useState(true)
  const { height } = Dimensions.get('window')

  const { data, isLoading } = useApiAction({
    isProgressPage: true,
  })

  const allSteps = data?.layout?.step || data?.metadata?.step

  const variantRecommendationCard = data?.variantRecommendationCard

  const openPrivacyPolicy = () => {
    setShowTnCBottomSheet({
      show: true,
      uri: privacyPolicyForExperianLink,
    })
    setShowSpinner(true)
  }

  const hideSpinnerLoader = () => {
    setShowSpinner(false)
  }

  const onDismiss = () => {
    setShowTnCBottomSheet({
      show: false,
      uri: '',
    })
    setShowSpinner(false)
  }

  return (
    <ScrollView
      style={{
        backgroundColor: '#020E1E',
        flex: 1,
      }}
      showsVerticalScrollIndicator={false}
    >
      {isLoading ? (
        <LoadingScreen
          props={{
            title: '',
          }}
        />
      ) : (
        <>
          <LoanHeaders
            hash={hash}
            variantRecommendationCard={variantRecommendationCard}
          />

          <Stack
            style={{
              marginTop: -1,
              height: DEVICE_HEIGHT * 0.75,
              justifyContent: 'space-between',
            }}
            className="rounded-t-2xl bg-white"
          >
            <Stack className="justify-start">
              <H6 className="m-3 text-xl">{title}</H6>
              <Stack className="ml-3 mt-3">
                <StepsIndicator
                  stepCount={allSteps?.total}
                  currentPosition={
                    allSteps?.total === allSteps?.current
                      ? allSteps?.current
                      : allSteps?.current - 1
                  }
                  labels={steps}
                  onPress={() => {
                    navigation.replace('BrandsCC', {
                      state: data?.data?.data?.state,
                      hash,
                    })
                  }}
                />
                {guideLines ? (
                  <Stack className="mb-3 mr-3 rounded-lg border-2 border-[#ECEFF5] bg-[#FAFBFE]">
                    <Stack className="bg-[#EBF3FF] p-2">
                      <SH3 className=" text-sm">{guideLines?.title}</SH3>
                    </Stack>
                    <Stack className="w-full p-3">
                      {guideLines?.guideLinesList?.map(
                        ({ Icon, text }, index) => (
                          <XStack
                            key={index}
                            className={`items-center ${
                              index === 0 ? 'pb-1' : ''
                            }`}
                          >
                            <Stack className="mr-1">
                              <Icon width={18} height={18} />
                            </Stack>
                            <B4 className="text-grays-70 ml-2">{text}</B4>
                          </XStack>
                        )
                      )}
                    </Stack>
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
            <Stack className="justify-end">
              <Stack className="bg-[#F5F7FB] p-3">
                <B4 className="text-grays-80 pb-1 text-sm">
                  {privacyNote?.title}
                </B4>
                <Stack className="w-11/12 items-start pl-2">
                  {privacyNote?.pcNotes.map((item, index) => (
                    <XStack key={index} className="pb-2">
                      <B4 className="pr-1 text-xs text-[#596379]">
                        {item?.prefix}
                      </B4>
                      <B4 className="text-xs tracking-wide text-[#596379]">
                        {item?.text}
                        {item?.link && (
                          <B4
                            onPress={openPrivacyPolicy}
                            className="text-primary-100 text-xs tracking-wide underline"
                          >
                            {' '}
                            {item?.link}
                          </B4>
                        )}
                      </B4>
                    </XStack>
                  ))}
                </Stack>
              </Stack>
              <View className=" bg-white">
                <ActionButton
                  title={
                    allSteps?.current === 1
                      ? i18n.t('loanApplications.startLoanApplication')
                      : i18n.t('loanApplications.resumeLoanApplication')
                  }
                  disabled={false}
                  action={'STATE_MACHINE_POST'}
                  payload={null}
                />
              </View>
            </Stack>
          </Stack>
        </>
      )}
      <BottomSheet visible={showTnCBottomSheet.show} onDismiss={onDismiss}>
        <Stack style={{ height: height / 0.9 }}>
          {showSpinner && <ActivityIndicator />}
          <PDFViewer uri={showTnCBottomSheet?.uri} onLoad={hideSpinnerLoader} />
        </Stack>
      </BottomSheet>
    </ScrollView>
  )
}

export default LoanApplicationDetails
